export const WIDGET_MESSAGE_ID_PREFIX = 'wid';

const UUID_DASHED =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const UUID_COMPACT = /^[0-9a-f]{32}$/i;
const WIDGET_MESSAGE_ID = /^wid[0-9a-f]{32}$/i;

export function isWidgetMessageId(value: string): boolean {
  return WIDGET_MESSAGE_ID.test(value);
}

function uuidWithoutHyphens(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID().replace(/-/g, '');
  }

  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i += 1) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

/** Client send id: `wid` + UUID v4 without hyphens. */
export function createWidgetMessageId(): string {
  return `${WIDGET_MESSAGE_ID_PREFIX}${uuidWithoutHyphens()}`;
}

/** Deterministic send id for greeting history rows (stable across retries). */
export function createStableWidgetMessageId(seed: string): string {
  let h0 = 0x811c9dc5;
  let h1 = 0x811c9dc5 ^ 0x9e3779b9;
  let h2 = 0x811c9dc5 ^ 0xc2b2ae35;
  let h3 = 0x811c9dc5 ^ 0x27d4eb2f;

  for (let i = 0; i < seed.length; i += 1) {
    const c = seed.charCodeAt(i);
    h0 = Math.imul(h0 ^ c, 0x01000193);
    h1 = Math.imul(h1 ^ (c + i), 0x01000193);
    h2 = Math.imul(h2 ^ (c * 31), 0x01000193);
    h3 = Math.imul(h3 ^ (c ^ i), 0x01000193);
  }

  const hex =
    (h0 >>> 0).toString(16).padStart(8, '0') +
    (h1 >>> 0).toString(16).padStart(8, '0') +
    (h2 >>> 0).toString(16).padStart(8, '0') +
    (h3 >>> 0).toString(16).padStart(8, '0');

  return `${WIDGET_MESSAGE_ID_PREFIX}${hex.slice(0, 32)}`;
}

/** Keep a stored id if it already matches the send contract. */
export function resolveWidgetMessageId(value?: string | null): string {
  const trimmed = value?.trim();
  if (!trimmed) return createWidgetMessageId();
  if (isWidgetMessageId(trimmed)) return trimmed;
  if (UUID_DASHED.test(trimmed)) {
    return `${WIDGET_MESSAGE_ID_PREFIX}${trimmed.replace(/-/g, '')}`;
  }
  if (UUID_COMPACT.test(trimmed)) {
    return `${WIDGET_MESSAGE_ID_PREFIX}${trimmed}`;
  }
  return createWidgetMessageId();
}
